import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, List, Typography, Space, Popconfirm, Tag, Empty, Row, Col, Spin } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { AppDispatch, RootState } from '../store';
import { fetchRankings, deleteRanking } from '../features/rankings/rankingsSlice';
import styled from 'styled-components';

const { Title, Paragraph, Text } = Typography;

const CoverImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
`;

const RankingTitle = styled(Typography.Title)`
  font-size: 16px !important;
  margin-bottom: 8px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
`;

const RankingDescription = styled(Typography.Paragraph)`
  font-size: 14px !important;
  margin-bottom: 12px !important;
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

// 定义基础 Ranking 接口
interface Ranking {
  id: string;
  title: string;
  description: string;
  type: string;
  coverUrl?: string;
  updatedAt?: string;
  clkCnt?: number;
}

// 添加 Ranking 接口或扩展现有接口
interface RankingWithTags extends Ranking {
  tags?: string[];
}

const RankingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rankings, status, error } = useSelector((state: RootState) => state.rankings);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchRankings());
    }
  }, [status, dispatch]);

  const handleDelete = async (id: string) => {
    await dispatch(deleteRanking(id));
  };

  if (status === 'loading') {
    return <Spin tip="加载中..." />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={2} style={{ margin: 0 }}>排行榜管理</Title>
        <Button type="primary" icon={<PlusOutlined />}>
          <Link to="/rankings/create">创建排行榜</Link>
        </Button>
      </div>

      {rankings.length === 0 ? (
        <Empty description="暂无排行榜，点击上方按钮创建" />
      ) : (
        <Row gutter={[16, 16]}>
          {rankings.map(ranking => (
            <Col xs={24} sm={12} md={8} lg={6} key={ranking.id}>
              <Card
                hoverable
                cover={
                  ranking.coverUrl ? (
                    <CoverImage src={ranking.coverUrl} alt={ranking.title} />
                  ) : null
                }
                actions={[
                  <Button key="edit" type="link" icon={<EditOutlined />}>
                    <Link to={`/rankings/${ranking.id}/edit`}>编辑</Link>
                  </Button>,
                ]}
              >
                <RankingTitle level={5}>
                  <Link to={`/rankings/${ranking.id}`}>{ranking.title}</Link>
                </RankingTitle>
                
                <RankingDescription ellipsis={{ rows: 2 }}>
                  {ranking.description}
                </RankingDescription>
                
                <StatsRow>
                  <span>
                    <img 
                      src="/assets/image/click_48.png" 
                      alt="点击" 
                      className="icon-view" 
                    />
                    {(ranking as any).clkCnt || 0} 次浏览
                  </span>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {ranking.updatedAt ? new Date(ranking.updatedAt).toLocaleDateString() : ''}
                  </Text>
                </StatsRow>
                
                {/* <TagsRow>
                  <Tag color="blue">{ranking.type}</Tag>
                  {((ranking as RankingWithTags).tags || []).map((tag: string, index: number) => (
                    <Tag key={index}>{tag}</Tag>
                  ))}
                </TagsRow> */}
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default RankingsPage; 